/**
 * 인증이 새로워질 때까지 기다린다 — 세션 스트림이 만료 토큰(UNAUTHENTICATED)을 받았을 때 쓴다.
 *
 * 인증 서버가 잠시 답하지 못하면 토큰은 갱신되지 못한 채 만료되고, 그 사이 다시 붙으려던 세션 스트림은 거절된다. 그것은 끝이
 * 아니다 — 인증 서버가 돌아오면 예약된 재갱신이 새 토큰을 받는다(oidc.ts retryRefreshLater). 그때(TOKEN_REFRESHED·SIGNED_IN)
 * 다시 붙으면 된다. 사건을 놓쳤을 때(이 구독 전에 이미 갱신됐다)를 위해 상한을 둔다. 로그아웃은 세션 수명 신호를 끊어 끝난다.
 * 실측 2026-10-15(로컬 웹 → 스테이지 BFF·IdP): 이 기다림 없이 스트림이 «영구 오류» 로 멈춰, 토큰이 갱신돼도 새로고침 전까지
 * 화면이 굳었다(완주한 실행이 RUNNING, «재접속 중» 칩도 없이).
 */
import { getBackend } from '@/app/services/backend';
import type { AuthChangeEvent } from '@/app/services/backend/types';

/** 놓친 사건 대비 상한 — 일시 장애 재갱신 간격(oidc.ts REFRESH_RETRY_MS)과 같다 */
export const AUTH_RENEWAL_FALLBACK_MS = 30_000;

/** 인증이 새로워졌다는 사건 — 새 토큰이 저장됐다 */
const RENEWED: ReadonlySet<AuthChangeEvent> = new Set<AuthChangeEvent>(['TOKEN_REFRESHED', 'SIGNED_IN']);

export function waitForAuthRenewal(signal: AbortSignal, fallbackMs = AUTH_RENEWAL_FALLBACK_MS): Promise<void> {
  return new Promise<void>((resolve) => {
    let unsubscribe: (() => void) | null = null;
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      unsubscribe?.();
      resolve();
    };
    const timer = setTimeout(done, fallbackMs);
    signal.addEventListener('abort', done, { once: true });
    if (signal.aborted) {
      done();
      return;
    }
    void getBackend()
      .then((backend) => {
        if (settled) return;
        const subscription = backend.auth.onAuthStateChange((event) => {
          if (RENEWED.has(event)) done();
        });
        unsubscribe = () => subscription.unsubscribe();
        if (settled) unsubscribe();
      })
      .catch((err) => {
        // 사건을 들을 수 없다 — 상한까지 기다린 뒤 다시 붙어 본다
        console.warn('[authRenewal] 인증 사건을 구독하지 못했다 — 상한까지 기다린다:', err);
      });
  });
}
